/**
 * ResearchCenterTable Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A sophisticated table interface for research articles with advanced sorting and modal integration.
 * Built with react-table for professional data presentation and user interaction.
 * 
 * CORE FEATURES:
 * - Professional table layout with react-table integration
 * - Advanced sorting capabilities with visual sort indicators
 * - Search query highlighting with HTML injection
 * - Dynamic category color coding using IFIA brand colors
 * - Modal system for detailed article viewing
 * - Responsive design with mobile-optimized layouts
 * - Empty state handling with user guidance
 * 
 * TABLE FUNCTIONALITY:
 * - Column-based sorting with ascending/descending states
 * - Visual sort indicators with FontAwesome icons
 * - Sortable and non-sortable column configuration
 * - Professional table styling with hover effects
 * - Row click handling for article selection
 * - Accessible table navigation with ARIA attributes
 * 
 * SEARCH INTEGRATION:
 * - Real-time search query highlighting in content
 * - HTML injection for highlighted text with custom CSS classes
 * - Multi-field highlighting (title, summary, categories)
 * - Context-aware highlighting that preserves formatting
 * 
 * CATEGORY SYSTEM:
 * - Advanced category color mapping using IFIA brand colors
 * - Color consistency with other research components
 * - Visual hierarchy with category and strategic tags
 * - Hash-based color assignment for unmapped categories
 * - Professional tag styling with proper spacing
 * 
 * MODAL INTEGRATION:
 * - Professional modal system for article details
 * - State management for modal visibility
 * - Article data passing with complete information
 * - Modal close and state cleanup handling
 * 
 * CONTENT PROCESSING:
 * - Text truncation with tooltip previews
 * - URL removal for clean summary display
 * - Author and company information presentation
 * - Date formatting with calendar icons
 * - Rich content rendering with proper escaping
 * 
 * TECHNICAL IMPLEMENTATION:
 * - React-table hooks for advanced table functionality
 * - Custom column definitions with cell renderers
 * - State management for sorting and modal operations
 * - Tooltip integration with Tippy.js
 * - Responsive design with CSS Grid and Flexbox
 * - Production-ready with comprehensive error handling
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of research article objects
 * @param {string} props.data[].id - Unique article identifier
 * @param {string} props.data[].title - Article title
 * @param {string} props.data[].displaySummary - Article summary/description
 * @param {string} props.data[].issueDate - Publication date
 * @param {string} props.data[].weeklyCategory - Primary category classification
 * @param {string} props.data[].strategicCategory - Strategic category classification
 * @param {string} props.data[].author - Article author name
 * @param {string} props.data[].submittedAuthor - Submitted author name
 * @param {string} props.data[].submittedCompany - Company/organization name
 * @param {number} props.visibleCount - Display count limit (default: 5)
 * @param {string} props.searchQuery - Search query for highlighting (default: '')
 * @returns {JSX.Element} Research articles table interface
 */
import React, { useEffect, useState } from 'react';
import { useTable, useSortBy } from 'react-table';
import { truncateText, removeURLs } from '../../utils/textUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortUp, faSortDown, faSort } from '@fortawesome/free-solid-svg-icons';
import Modal from '../../modals/NewsModal';
import './ResearchCenterTable.css';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

/**
 * ResearchCenterTable Component
 * 
 * Professional table interface for research articles with sorting, search highlighting, and modal integration.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Research articles array
 * @param {number} props.visibleCount - Display count limit
 * @param {string} props.searchQuery - Search query for highlighting
 * @returns {JSX.Element} Research articles table interface
 */
const ResearchCenterTable = ({ data, visibleCount = 5, searchQuery = '' }) => {
    const [showModal, setShowModal] = useState(false);
    const [activeArticle, setActiveArticle] = useState(null);

    // Get category color class matching UnifiedNews color scheme
    const getCategoryColor = (category) => {
        if (!category) return 'ifia-green'; // Default to IFIA green
        
        // Use the same category mapping as UnifiedNews (replaceCss creates class names like "AdvocacyRegulatory")
        const normalizedCategory = category.replace(/&/g, "").replace(/\s+/g, "");
        
        const colorMap = {
            // Exact matches from UnifiedNews
            'Intro': 'ifia-dark-green',
            'AdvocacyRegulatory': 'ifia-green',
            'Events': 'ifia-orange',
            'EngagementsCommunications': 'ifia-orange',
            'EngagementCommunications': 'ifia-purple',
            'CapabilityInnovation': 'ifia-dark-green',
            'WorkingGroupUpdates': 'ifia-blue',
            
            // Additional category mappings using IFIA colors
            'Tax': 'ifia-green',
            'ESG': 'ifia-dark-green',
            'FinancialLiteracy': 'ifia-orange',
            'Digitalisation': 'ifia-blue',
            'Product': 'ifia-purple',
            'LegalStructures': 'ifia-green',
            'Innovation': 'ifia-dark-green',
            'Strategy': 'ifia-blue',
            'Regulatory': 'ifia-green',
            'MarketUpdates': 'ifia-orange',
            'IndustryNews': 'ifia-purple',
            
            // Fallback patterns
            'Legal': 'ifia-green',
            'Market': 'ifia-orange',
            'Industry': 'ifia-purple',
            'Financial': 'ifia-orange',
            'Digital': 'ifia-blue',
            'Compliance': 'ifia-green',
            'Policy': 'ifia-green',
            'Technology': 'ifia-blue',
            'Sustainability': 'ifia-dark-green',
            'Research': 'ifia-purple'
        };
        
        // First try exact match
        if (colorMap[normalizedCategory]) {
            return colorMap[normalizedCategory];
        }
        
        // Then try partial matches
        const normalizedLower = normalizedCategory.toLowerCase();
        const matchedKey = Object.keys(colorMap).find(key => 
            normalizedLower.includes(key.toLowerCase()) || key.toLowerCase().includes(normalizedLower)
        );
        
        if (matchedKey) {
            return colorMap[matchedKey];
        }
        
        // Fallback to IFIA color rotation
        const ifiaColors = ['ifia-green', 'ifia-dark-green', 'ifia-orange', 'ifia-blue', 'ifia-purple'];
        const hash = normalizedCategory.split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
        }, 0);
        
        return ifiaColors[Math.abs(hash) % ifiaColors.length];
    };

    // Get category icon matching UnifiedNews system
    const getCategoryIcon = (category) => {
        if (!category) return 'fa-tag'; // Default icon
        
        // Use the same category mapping as UnifiedNews (replaceCss creates class names like "AdvocacyRegulatory")
        const normalizedCategory = category.replace(/&/g, "").replace(/\s+/g, "");
        
        const iconMap = {
            // Exact matches from UnifiedNews
            'Intro': 'fa-star',
            'AdvocacyRegulatory': 'fa-gavel',
            'Events': 'fa-calendar-alt',
            'EngagementsCommunications': 'fa-comments',
            'EngagementCommunications': 'fa-comments',
            'CapabilityInnovation': 'fa-lightbulb',
            'WorkingGroupUpdates': 'fa-users',
            
            // Additional category mappings
            'Tax': 'fa-calculator',
            'ESG': 'fa-leaf',
            'FinancialLiteracy': 'fa-graduation-cap',
            'Digitalisation': 'fa-digital-tachograph',
            'Product': 'fa-box',
            'LegalStructures': 'fa-balance-scale',
            'Innovation': 'fa-lightbulb',
            'Strategy': 'fa-chess',
            'Regulatory': 'fa-gavel',
            'MarketUpdates': 'fa-chart-line',
            'IndustryNews': 'fa-industry',
            
            // Fallback patterns
            'Legal': 'fa-balance-scale',
            'Market': 'fa-chart-line',
            'Industry': 'fa-industry',
            'Financial': 'fa-euro-sign',
            'Digital': 'fa-digital-tachograph',
            'Compliance': 'fa-shield-alt',
            'Policy': 'fa-file-contract',
            'Technology': 'fa-microchip',
            'Sustainability': 'fa-leaf',
            'Research': 'fa-search'
        };
        
        // First try exact match
        if (iconMap[normalizedCategory]) {
            return iconMap[normalizedCategory];
        }
        
        // Then try partial matches
        const normalizedLower = normalizedCategory.toLowerCase();
        const matchedKey = Object.keys(iconMap).find(key => 
            normalizedLower.includes(key.toLowerCase()) || key.toLowerCase().includes(normalizedLower)
        );
        
        if (matchedKey) {
            return iconMap[matchedKey];
        }
        
        // Fallback to default tag icon
        return 'fa-tag';
    };

    const handleShow = (itemData) => {
        setActiveArticle(itemData);
        setShowModal(true);
    };

    const handleClose = () => {
        setShowModal(false);
        setActiveArticle(null);
    };

    const columns = React.useMemo(
        () => [
            {
                Header: 'Date',
                accessor: 'issueDate',
                Cell: ({ row }) => (
                    <div className="table-date">
                        <i className="fas fa-calendar-alt"></i>
                        <span>{row.original.displayDate || row.original.issueDate}</span>
                    </div>
                )
            },
            {
                Header: 'Article & Summary',
                accessor: 'title',
                Cell: ({ row }) => (
                    <div className="table-content">
                        <h4 className="table-title">
                            {searchQuery.trim() ? (
                                <span dangerouslySetInnerHTML={{ 
                                    __html: row.original.title.replace(
                                        new RegExp(searchQuery.trim(), 'gi'), 
                                        match => `<span class="highlight-match">${match}</span>`
                                    )
                                }} />
                            ) : (
                                row.original.title
                            )}
                        </h4>
                        {row.original.displaySummary && row.original.displaySummary.trim() && (
                            <Tippy content={removeURLs(row.original.displaySummary)}>
                                <p className="table-summary">
                                    {searchQuery.trim() ? (
                                        <span dangerouslySetInnerHTML={{ 
                                            __html: removeURLs(truncateText(row.original.displaySummary, 150)).replace(
                                                new RegExp(searchQuery.trim(), 'gi'), 
                                                match => `<span class="highlight-match">${match}</span>`
                                            )
                                        }} />
                                    ) : (
                                        removeURLs(truncateText(row.original.displaySummary, 150))
                                    )}
                                </p>
                            </Tippy>
                        )}
                        <div className="table-tags">
                            {row.original.sourceType && (
                                <span className={`research-tag source-tag research-tag-${row.original.sourceType === 'news' ? 'ifia-blue' : 'ifia-purple'}`}>
                                    <i className={`fas ${row.original.sourceType === 'news' ? 'fa-newspaper' : 'fa-star'}`}></i>
                                    {row.original.sourceType === 'news' ? 'News' : 'Featured'}
                                </span>
                            )}
                            {row.original.weeklyCategory && (
                                <span className={`research-tag category-tag research-tag-${getCategoryColor(row.original.weeklyCategory)}`}>
                                    <i className={`fas ${getCategoryIcon(row.original.weeklyCategory)}`}></i>
                                    {row.original.weeklyCategory}
                                </span>
                            )}
                            {row.original.strategicCategory && row.original.strategicCategory !== row.original.weeklyCategory && (
                                <span className={`research-tag strategic-tag research-tag-${getCategoryColor(row.original.strategicCategory)}`}>
                                    <i className={`fas ${getCategoryIcon(row.original.strategicCategory)}`}></i>
                                    {row.original.strategicCategory}
                                </span>
                            )}
                        </div>
                    </div>
                )
            },
            {
                Header: 'Type',
                accessor: 'sourceType',
                Cell: ({ row }) => (
                    <div className="table-type-simple">
                        {row.original.sourceType === 'news' ? 'News' : 'Featured'}
                    </div>
                )
            },
            {
                Header: 'Actions',
                accessor: 'actions',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div className="table-actions">
                        <button 
                            className="read-more-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleShow(row.original);
                            }}
                        >
                            <span>Read</span>
                            <i className="fas fa-arrow-right"></i>
                        </button>
                    </div>
                )
            }
        ],
        [searchQuery]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow
    } = useTable(
        {
            columns,
            data: data,
            disableSortRemove: true
        },
        useSortBy
    );

    return (
        <div className="research-centre-table">
            {data.length === 0 ? (
                <div className="no-results">
                    <div className="no-results-content">
                        <i className="fas fa-search"></i>
                        <h3>No research articles found</h3>
                        <p>Try adjusting your search criteria or filters to see more results.</p>
                    </div>
                </div>
            ) : (
                <div className="table-container">
                    <table {...getTableProps()} className="research-table">
                        <thead>
                            {headerGroups.map(headerGroup => {
                                const { key: headerKey, ...headerProps } = headerGroup.getHeaderGroupProps();
                                return (
                                    <tr {...headerProps}>
                                        {headerGroup.headers.map(column => {
                                            const { key: columnKey, ...columnProps } = column.getHeaderProps(column.getSortByToggleProps());
                                            return (
                                                <th 
                                                    {...columnProps} 
                                                    className={`col-${column.id} ${column.isSorted ? 'sorted' : ''}`}
                                                    data-sortable={!column.disableSortBy}
                                                    data-sorted={column.isSorted}
                                                    aria-sort={
                                                        column.isSorted 
                                                            ? (column.isSortedDesc ? 'descending' : 'ascending')
                                                            : 'none'
                                                    }
                                                >
                                                    <div className="header-content">
                                                        <span className="header-text">{column.render('Header')}</span>
                                                        {!column.disableSortBy && (
                                                            <span className={`sort-icon ${column.isSorted ? 'active' : ''}`}>
                                                                {column.isSorted ? (
                                                                    column.isSortedDesc ? 
                                                                        <FontAwesomeIcon icon={faSortDown} title="Sorted Descending" /> : 
                                                                        <FontAwesomeIcon icon={faSortUp} title="Sorted Ascending" />
                                                                ) : (
                                                                    <FontAwesomeIcon icon={faSort} title="Click to Sort" />
                                                                )}
                                                            </span>
                                                        )}
                                                        {column.disableSortBy && (
                                                            <span className="sort-icon disabled" style={{opacity: 0.3}}>
                                                                <i className="fas fa-grip-vertical" title="Not Sortable"></i>
                                                            </span>
                                                        )}
                                                    </div>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {rows.map(row => {
                                prepareRow(row);
                                const { key: rowKey, ...rowProps } = row.getRowProps();
                                return (
                                    <tr {...rowProps} 
                                        className="table-row" 
                                        onClick={() => handleShow(row.original)}
                                    >
                                        {row.cells.map(cell => {
                                            const { key: cellKey, ...cellProps } = cell.getCellProps();
                                            return (
                                                <td {...cellProps}>{cell.render('Cell')}</td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {activeArticle && showModal && (
                <Modal data={activeArticle} show={showModal} onClose={handleClose} allowFullscreen={false} />
            )}
        </div>
    );
};

export default ResearchCenterTable;
